import { External, M } from '../../external';
import { formatMoney, makeWebsiteRequest, withTax } from '../../Common/tools';
import ValueAddon from './ValueAddon.svelte';
import FunnelAddon from './FunnelAddon.svelte';
import AdjustValueAddon from './AdjustValueAddon.svelte';
import MarketingSubscribersAddon from './MarketingSubscribersAddon.svelte';
import CommunityAddon from './CommunityAddon.svelte';
import VideoAddon from './VideoAddon.svelte';
import ToggleAddon from './ToggleAddon.svelte';
import MeteredAddon from './MeteredAddon.svelte';

let newAddonPriceSummary = (price, currency, hasMultiplePrices) =>
  `${hasMultiplePrices ? M.FROM_RANGE + ' ' : ''}${formatMoney(price, currency)}/${M.MONTH}`;

let myAddonPriceSummary = (addon, tax, currency, includeValue) =>
  `${includeValue ? addon.value + ' x ' : ''}${formatMoney(withTax(addon.monthlyPrice, tax), currency)}/${M.MONTH}`;

let valueToStr = (value) => value === 999 ? M.UNLIMITED : `+${External.WebPlatform.addThousandsSeparator(value)}`;

let getVideoAddonIdxByValue = (value) => {
  return External.Website.addons.filter(addon => addon.type === 'VIDEO').map(addon => {
    return {
      value: addon.value,
      monthlyPrice: addon.monthlyPrice,
      videos: ADDON_VIDEOS_LIMIT[addon.value] || 0
    };
  }).sort((a, b) => a.videos === b.videos ? a.monthlyPrice - b.monthlyPrice : a.videos - b.videos).findIndex(addon => addon.value === value);
};

export let ADDON_ORDER = [
  'WEBSITE', 'FUNNELS', 'ECOMMERCE', 'COMMUNITY', 'VIDEO', 'AFFILIATE', 'EMAIL_MARKETING', 'MARKETING_EMAILS', 'ADMIN', 'BOOKING', 'BANDWIDTH'
];

export let ADDON_VIDEOS_LIMIT = [0, 30, 100, 999, 100, 200];

export let LABELS = {
  WEBSITE: {
    name: M.WEBSITE,
    title: M.EXTRA_WEBSITES,
    initialAddDescription: M.EXTRA_WEBSITES_DESC,
    description: M.EXTRA_WEBSTIES_AMOUNT,
    summary: (number) => {
      return number === 1 ? M.ONE_EXTRA_WEBSITE : External.format(M.N_EXTRA_WEBSITES, 'n', number);
    },
    features: [
      M.ONE_EXTRA_WEBSITE,
      M.UNLIMITED_PAGES,
      M.UNLIMITED_POPUPS,
      M.CUSTOM_DOMAIN_CONNECTION
    ],
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: valueToStr
  },
  FUNNELS: {
    name: M.FUNNELS,
    title: M.FUNNELS,
    description: M.FUNNELS_ADDON_DESC,
    summary: (number) => {
      if (number === 999) {
        return External.format(M.N_EXTRA_FUNNELS, 'n', M.UNLIMITED).replace('+', '');
      }
      return number === 1 ? M.ONE_EXTRA_FUNNEL : External.format(M.N_EXTRA_FUNNELS, 'n', number);
    },
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: valueToStr
  },
  EMAIL_MARKETING: {
    name: M.MARKETING_SUBSCRIBERS,
    title: M.MARKETING_SUBSCRIBERS,
    description: M.MARKETING_SUBSCRIBERS_DESC,
    summary: (number) => External.format(M.N_EXTRA_SUBSCRIBERS, 'n', External.WebPlatform.addThousandsSeparator(number)),
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: valueToStr
  },
  ECOMMERCE: {
    name: M.ECOMMERCE,
    title: M.ECOMMERCE,
    description: M.ECOMMERCE_ADDON_DESC,
    summary: (number) => M.UNLIMITED_ECOMMERCE,
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: () => '∞',
    features: [
      M.UNLIMITED_PRODUCTS,
      M.CUSTOM_CHECKOUTS,
      M.ECOMMERCE_ANALYTICS,
      M.UPSELLS_BUMPS_DOWNSELLS,
      M.SUBSCRIPTIONS_AND_MANAGEMENT,
      M.DISCOUNT_CODES,
      M.MORE_ECOMMERCE_FEATURES
    ]
  },
  COMMUNITY: {
    name: M.COMMUNITY,
    title: M.COMMUNITY,
    description: M.COMMUNITY_ADDON_DESC,
    summary: (number) => {
      switch (number) {
        case 1:
          return formatNumber(1, M.ONE_CIRCLE) + ', ' + formatNumber(1000, '', M.X_MEMBERS);
        case 2:
          return formatNumber(5, '', M.CIRCLE_NUMBER) + ', ' + formatNumber(5000, '', M.X_MEMBERS);
        case 3:
          return M.UNLIMITED_CIRCLES + ', ' + formatNumber(10000, '', M.X_MEMBERS);
        case 4:
          return M.UNLIMITED_CIRCLES + ', ' + M.UNLIMITED_MEMBERS;
        default:
          return M.COMMUNITY;
      }
    },
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: (value) => `T${value}`
  },
  VIDEO: {
    name: M.VIDEO_HOSTING,
    title: M.VIDEO_HOSTING,
    description: M.VIDEO_HOSTING_DESC,
    summary: (value) => {
      const index = getVideoAddonIdxByValue(value);
      return index < 0 ? '-' : External.format(M.TIER, 'x', index + 1);
    },
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: (value) => {
      const index = getVideoAddonIdxByValue(value);
      return index < 0 ? '-' : `T${index + 1}`;
    }
  },
  AFFILIATE: {
    name: M.AFFILIATE,
    title: M.AFFILIATE,
    description: M.AFFILIATE_ADDON_DESC,
    summary: (number) => M.AFFILIATE_ADDON_DESC,
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: valueToStr
  },
  ADMIN: {
    name: M.ADMIN,
    title: M.ADMINS,
    initialAddDescription: M.ADMINS_ADDON_DESC,
    description: M.ADMINS_ADDON_NUMBER,
    summary: (number) => {
      return number === 1 ? M.ONE_ADMIN : External.format(M.N_ADMINS, 'n', number);
    },
    features: [
      M.ONE_ADMIN_SEAT,
      M.ADMIN_FEATURES
    ],
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: valueToStr
  },
  BOOKING: {
    name: M.APPOINTMENT_OPERATOR,
    title: M.APPOINTMENT_OPERATOR,
    initialAddDescription: M.APP_OPERATOR_DESC,
    description: M.APP_OPERATOR_NUMBER,
    summary: (number) => {
      return number === 1 ? M.ONE_OPERATOR : External.format(M.N_OPERATORS, 'n', number);
    },
    features: [
      M.ONE_APP_OPERATOR,
      M.UNLIMITED_APP_SERVICES

    ],
    newAddonPriceSummary: newAddonPriceSummary,
    myAddonPriceSummary: myAddonPriceSummary,
    valueToStr: valueToStr
  },
  BANDWIDTH: {
    name: M.EXTRA_BANDWIDTH,
    title: M.EXTRA_BANDWIDTH,
    description: M.EXTRA_BANDWIDTH_DESC,
    summary: (number) => M.UNLIMITED_BANDWIDTH,
    meteredPricingLabel: M.GB_AFTER_LIMIT,
    features: [
      M.UNLIMITED_BANDWIDTH,
      M.PAY_PER_USE
    ],
    newAddonPriceSummary: (price, currency, hasMultiplePrices) =>
      External.format(M.X_GB_BANDTHWIDTH, 'x', formatMoney(price, currency)),
    myAddonPriceSummary: (addon, tax, currency) =>
      External.format(M.X_GB_BANDTHWIDTH, 'x', formatMoney(withTax(addon.monthlyPrice, tax), currency)),
    valueToStr: (value) => planValueToStr(value, 'bandwidth')
  },
  MARKETING_EMAILS: {
    name: M.EXTRA_MARKETING_EMAILS,
    title: M.EXTRA_MARKETING_EMAILS,
    description: M.EXTRA_MARKETING_EMAILS_DESC,
    summary: (number) => M.UNLIMITED_EMAILS,
    meteredPricingLabel: M.PER_1000,
    features: [
      M.UNLIMITED_EMAILS,
      M.PAY_PER_USE
    ],
    newAddonPriceSummary: (price, currency, hasMultiplePrices) =>
      External.format(M.X_PER_1000, 'x', formatMoney(price, currency)),
    myAddonPriceSummary: (addon, tax, currency) =>
      External.format(M.X_PER_1000, 'x', formatMoney(withTax(addon.monthlyPrice, tax), currency)),
    valueToStr: (value) => External.WebPlatform.addThousandsSeparator(value)
  }
};

export let showChangeAddonUI = (addon, subscription) => {
  let width = 700;
  let component = null;
  switch (addon.type) {
    case 'WEBSITE':
    case 'ADMIN':
    case 'BOOKING':
      component = AdjustValueAddon;
      break;
    case 'FUNNELS':
      component = FunnelAddon;
      break;
    case 'EMAIL_MARKETING':
      component = MarketingSubscribersAddon;
      break;
    case 'COMMUNITY':
      component = CommunityAddon;
      width = 1300;
      break;
    case 'VIDEO':
      component = VideoAddon;
      width = Website.addons.filter(a => a.type === 'VIDEO').length > 3 ? 1450 : 1100;
      break;
  }
  if (!component) {
    return;
  }
  return showPopup(component, addon, addon.type, subscription, width);
};

export let getMarketingEmailsAddon = (subscription) => {
  return {
    id: 0,
    type: 'MARKETING_EMAILS',
    value: 1,
    numOfAddonsOfType: 1,
    monthlyPrice: subscription.emailOverchargeFee
  };
};

export let getSubscriptionDetails = async () => {
  let result = await makeWebsiteRequest({
    action: 'getInvoicingData'
  });
  return result.ok ? result.data : null;
};

export let showActivateAddonUI = async (type, currentSubscription, showLimitsReachedMessage, manuallyActivatedMessage) => {
  let checkedType = type === 'MARKETING_EMAILS' ? 'EMAIL_MARKETING' : type;
  if (External.Website.addons.filter(addon => addon.type === checkedType).length === 0) {
    External.Tools.showError(manuallyActivatedMessage);
    return;
  }

  if (!currentSubscription) {
    currentSubscription = await getSubscriptionDetails();
  }
  if (currentSubscription && currentSubscription.manuallyActivated && manuallyActivatedMessage) {
    External.Tools.showError(manuallyActivatedMessage);
    return;
  }

  let width = 700;
  let component = null;
  switch (type) {
    case 'WEBSITE':
    case 'ADMIN':
    case 'BOOKING':
      component = ValueAddon;
      break;
    case 'FUNNELS':
      component = FunnelAddon;
      break;
    case 'EMAIL_MARKETING':
      component = MarketingSubscribersAddon;
      break;
    case 'COMMUNITY':
      component = CommunityAddon;
      width = 1300;
      break;
    case 'VIDEO':
      component = VideoAddon;
      width = Website.addons.filter(a => a.type === 'VIDEO').length > 3 ? 1450 : 1100;
      break;
    case 'ECOMMERCE':
      //case 'AFFILIATE':
      component = ToggleAddon;
      break;
    case 'BANDWIDTH':
    case 'MARKETING_EMAILS':
      component = MeteredAddon;
      break;
  }
  if (!component) {
    return;
  }
  return showPopup(component, null, type, currentSubscription, width, showLimitsReachedMessage);
};

let showPopup = async (component, addon, type, subscription, width, showLimitsReachedMessage) => {
  return new Promise((resolve, reject) => {
    let result = null;
    External.Tools.showWindow({
      withDarkBackground: true,
      showCloseButton: false,
      noNav: true,
      noTitle: true,
      width: width || 700,
      isModal: false,
      forceAtRight: true,
      action: {
        onShow: (popup, context) => {
          popup.bindComponent(
            new component({
              target: popup.getContentArea(),
              props: {
                type: type,
                addon: addon,
                subscription: subscription,
                showLimitsReachedMessage: showLimitsReachedMessage,
                onChange: (subscriptionDetails) => {
                  result = subscriptionDetails;
                  popup.close();
                },
                onCancel: (goToBillingPage) => {
                  popup.close();
                  External.CPanel.goToUrl('/me/settings/billing', false);
                }
              }
            })
          );
        },
        onHide: () => {
          resolve(result);
        }
      }
    });
  });
};

export let enableAddon = (addon, value) => {
  if (addon.type === 'MARKETING_EMAILS') {
    return makeWebsiteRequest({
      action: 'setOverchargeSentEmails',
      data: {
        overcharge: true
      }
    });
  } else {
    return makeWebsiteRequest({
      action: 'enableAddon',
      data: {
        addon: addon.id,
        value: value
      }
    });
  }
};

export let disableAddon = async (addon) => {
  return new Promise((resolve, reject) => {
    External.Tools.prompt(M.DISABLE_ADDON, M.DISABLE_ADDON_PROMPT, async () => {
      let result;
      if (addon.type === 'MARKETING_EMAILS') {
        result = await makeWebsiteRequest({
          action: 'setOverchargeSentEmails',
          data: {
            overcharge: false
          }
        });
      } else {
        result = await makeWebsiteRequest({
          action: 'disableAddon',
          data: {
            type: addon.type
          }
        });
      }

      if (result.ok) {
        External.Tools.showAutohideMessage(1500, M.ADDON_REMOVAL_SUCCESS);
        resolve(result.data);
      } else {
        resolve(null);
      }
    });
  });
};

export let formatNumber = (number, single, multiple, unlimited) => {
  return number === 1 ? single : number === 999 ? unlimited : External.format(multiple, 'x', External.WebPlatform.addThousandsSeparator(number));
};

export let planValueToStr = (value, type) => {
  switch (type) {
    case 'bandwidth':
    case 'storage':
      let size = parseInt(value) / 1024;
      if (size < 1) {
        return '0';
      }
      for (let unit of ['KB', 'MB', 'GB']) {
        if (size < 1024) {
          size = size.toFixed(2);
          if (size.endsWith('.00')) {
            size = size.split('.')[0];
          }
          return `${size}${unit}`;
        }
        size /= 1024.0;
      }
      return `${size.toFixed(2)}TB`;
  }
  return External.WebPlatform.addThousandsSeparator(value);
};
